import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import EmptyState from "@/components/common/EmptyState";
import ResourceDrawer from "@/components/common/ResourceDrawer";
import { Button } from "@/components/ui/button";
import { Plus, Boxes, AlertTriangle, Upload } from "lucide-react";
import CsvImportDialog from "@/components/common/CsvImportDialog";

export default function Items() {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const [importOpen, setImportOpen] = useState(false);
    const load = async () => { const { data } = await api.get("/items", { params: { q: search || undefined } }); setItems(data); };
    useEffect(() => { load(); }, [search]);

    const fields = [
        { name: "name", label: "Name", span: 2 },
        { name: "sku", label: "SKU" },
        { name: "type", label: "Type", type: "select", options: [{ value: "goods", label: "Goods" }, { value: "service", label: "Service" }] },
        { name: "unit", label: "Unit" }, { name: "hsn", label: "HSN/SAC" },
        { name: "sales_price", label: "Sales price", type: "number" },
        { name: "purchase_price", label: "Purchase price", type: "number" },
        { name: "tax_rate", label: "GST %", type: "number" },
        { name: "opening_stock", label: "Opening stock", type: "number" },
        { name: "reorder_level", label: "Reorder level", type: "number" },
    ];
    const save = async (form) => {
        if (drawer.item) await api.put(`/items/${drawer.item.id}`, form);
        else await api.post(`/items`, form);
        await load();
    };

    return (
        <>
            <PageHeader kicker="INVENTORY" title="Items" subtitle="Goods and services with HSN/SAC, GST, and stock tracking."
                actions={<>
                    <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="item-import"><Upload className="w-4 h-4 mr-1.5" />Import CSV</Button>
                    <Button onClick={() => setDrawer({ open: true, item: null })} data-testid="item-new"><Plus className="w-4 h-4 mr-1.5" />New item</Button>
                </>} />
            <PageBody>
                <FilterBar search={search} onSearch={setSearch} />
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {items.length === 0 ? <EmptyState icon={Boxes} title="No items yet" actionLabel="New item" onAction={() => setDrawer({ open: true, item: null })} /> :
                        <table className="fin-table">
                            <thead><tr><th>Item</th><th>SKU/HSN</th><th>Type</th><th className="num">Sales price</th><th className="num">Stock</th><th>GST</th></tr></thead>
                            <tbody>
                                {items.map((i) => {
                                    const lowStock = i.track_inventory && i.current_stock <= i.reorder_level && i.reorder_level > 0;
                                    return (
                                        <tr key={i.id} className="cursor-pointer" onClick={() => setDrawer({ open: true, item: i })} data-testid={`item-row-${i.id}`}>
                                            <td><div className="font-medium">{i.name}</div><div className="text-[11px] text-muted-foreground">{i.unit}</div></td>
                                            <td className="font-mono text-[12px]">{i.sku || "—"}<div className="text-[11px] text-muted-foreground">HSN: {i.hsn || i.sac || "—"}</div></td>
                                            <td className="capitalize text-[12px]">{i.type}</td>
                                            <td className="num">{fmtMoney(i.sales_price)}</td>
                                            <td className="num">
                                                {i.track_inventory ? <span className={lowStock ? "text-destructive font-medium inline-flex items-center gap-1" : ""}>{lowStock && <AlertTriangle className="w-3.5 h-3.5" />}{i.current_stock} {i.unit}</span> : <span className="text-muted-foreground">—</span>}
                                            </td>
                                            <td>{i.tax_rate}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>}
                </div>
            </PageBody>
            <ResourceDrawer open={drawer.open} onClose={() => setDrawer({ open: false, item: null })} title={drawer.item ? "Edit item" : "New item"} fields={fields} initial={drawer.item || { type: "goods", unit: "Nos", tax_rate: 18, track_inventory: true }} onSave={save} />
            <CsvImportDialog
                open={importOpen}
                onClose={() => setImportOpen(false)}
                title="Import items"
                templateUrl="/items/import/template"
                previewUrl="/items/import/preview"
                commitUrl="/items/import"
                onImported={load}
                testIdPrefix="item-csv"
            />
        </>
    );
}
